// QV-4241 Dual Price — saveOrderPos payload conversion (Android OrderCreateViewModel toParseJson /
// getPayloadSubTotal / getPayloadDiscount / getLoyaltyJson / SaveOrderViewModel.setPayData).
//
// The cart is credit-priced. A one-shot NON-credit dual tender (cash / debit / EBT) saves CASH values
// (÷M, base tax); a one-shot CREDIT tender saves CARD values (component-wise card columns). Split
// orders keep the credit lines and book Σ leg NCA at order level.

import { roundHalfUp, toNum } from "./money";
import { isDualPrice, toBasePrice, surchargeMultiplier, giftCardFaceTotal } from "./dualPrice";

const r2 = (v) => roundHalfUp(Number.isFinite(v) ? v : 0);
const money = (v) => r2(v).toFixed(2);
const isPayoutOrGift = (l) => !!(l.isLotteryPayout || l.isScratcherPayout || l.isGiftCard);

/** "credit" | "cash" (cash / debit / EBT — every non-credit tender) | "split". */
export function dualTenderKind(tenders) {
  const list = (tenders || []).filter(Boolean);
  if (list.length > 1) return "split";
  const t = list[0];
  const pt = String((t && t.payType) || "").toLowerCase();
  return pt === "credit" || pt === "card" ? "credit" : "cash";
}

/** Android ncaListWeight — list (credit) value of a line; 0 for gift / payout rows. */
function ncaListWeight(l) {
  if (isPayoutOrGift(l)) return 0;
  const isOverride = toNum(l.orderUnitNewPrice) > 0 && toNum(l.bogoIdentifier) === 0 && !l.isBulkPriceApplied;
  const unit = isOverride ? toNum(l.orderUnitNewPrice) : toNum(l.catalogUnitPrice != null ? l.catalogUnitPrice : (l.model || {}).price);
  return unit * toNum(l.orderCount || 1);
}

/** Android getItemLevelCashPayDiscountMap — distribute `total` by weight; last non-zero row absorbs the rest. */
function distribute(total, weights) {
  const out = weights.map(() => 0);
  if (!(total > 0) || weights.length === 0) return out;
  const w = weights.map(r2);
  const totalW = r2(w.reduce((a, b) => a + b, 0));
  let assigned = 0;
  if (totalW === 0) {
    const even = r2(total / w.length);
    w.forEach((_, i) => {
      out[i] = i === w.length - 1 ? r2(total - assigned) : even;
      assigned = r2(assigned + out[i]);
    });
    return out;
  }
  let last = -1;
  w.forEach((v, i) => {
    if (v !== 0) last = i;
  });
  w.forEach((v, i) => {
    out[i] = i === last ? r2(total - assigned) : v === 0 ? 0 : r2((total * v) / totalW);
    assigned = r2(assigned + out[i]);
  });
  return out;
}

/**
 * Rewrite item_ses rows (aligned with `lines`) for a dual-price order. Mutates `items`.
 * @returns {{ cashPayDiscount: number }}
 */
export function applyDualItemSession(items, lines, p = {}) {
  const { cfg, dual, kind, loyalty = 0, couponDiscountValue = 0, mSubTotal = 0, taxRateInAmount = 0, perLineBaseDefaultTax } = p;
  if (!isDualPrice(cfg) || !dual || !Array.isArray(items)) return { cashPayDiscount: 0 };
  const pct = toNum(cfg.pct);
  const M = surchargeMultiplier(cfg);
  const credit = kind === "credit";
  const split = kind === "split";

  // item_nca_amt — Android itemNcaAmt (frac 1 for credit, 0 for cash; split blend = Phase C → 0).
  const cardFrac = credit ? 1 : 0;
  items.forEach((it, i) => {
    const l = lines[i] || {};
    if (isPayoutOrGift(l) || cardFrac <= 0) {
      it.item_nca_amt = "0.00";
      return;
    }
    const u = toNum(l.jsonNewPrice) / Math.max(toNum(l.orderCount), 1);
    it.item_nca_amt = money((u - toBasePrice(cfg, u)) * cardFrac);
  });

  // item_cash_pay_discount — Android getCashPayDiscount: one-shot non-credit = fullItemNcaGross.
  const cashTotal = !split && !credit && pct > 0 ? Math.max(r2(lines.reduce((s, l) => s + ncaListWeight(l), 0) * (pct / (100 + pct))), 0) : 0;
  const shares = distribute(cashTotal, lines.map(ncaListWeight));
  items.forEach((it, i) => {
    it.item_cash_pay_discount = money(shares[i] || 0);
  });
  if (split) return { cashPayDiscount: 0 };

  const hasDed = !!dual.hasDeduction;
  const creditDualConsistent = credit || !hasDed;
  const divisor = !credit && pct > 0 ? M : 1;
  const creditCardScale = !!dual.cardDerived && credit;
  const creditTax = toNum(taxRateInAmount);
  const ratio =
    divisor !== 1
      ? creditTax > 0
        ? toNum(dual.baseTax) / creditTax
        : 1 / divisor
      : creditCardScale && creditTax > 0
      ? toNum(dual.cardTax) / creditTax
      : 1;
  const baseMap = perLineBaseDefaultTax instanceof Map ? perLineBaseDefaultTax : new Map();
  const useBasePerItem = divisor !== 1 && toNum(loyalty) !== 0 && baseMap.size > 0;

  const coupon = toNum(couponDiscountValue);
  const totalCreditNet = Math.max(toNum(mSubTotal) - coupon, 0);
  const lpNcaFraction = pct / 100;
  const totalBaseNetLp = Math.max(Math.max(toNum(dual.baseSubTotal) - toNum(dual.baseOrderDiscount), 0) - toNum(loyalty), 0);
  const totalCreditNetLp = Math.max(totalCreditNet - toNum(loyalty) - toNum(loyalty) * lpNcaFraction, 0);
  const totalCardNet = Math.max(toNum(dual.cardSubTotal) - toNum(dual.cardOrderDiscount), 0);

  let cumCreditTax = 0;
  let cumScaledTax = 0;
  let cumOrdCredit = 0;
  let cumOrdBase = 0;
  let cumOrdCard = 0;
  let cumOrdCardRounded = 0;
  let cumNetDist = 0;
  let cumBaseNet = 0;
  let cumCardNetIn = 0;
  let cumCardNet = 0;

  items.forEach((it, i) => {
    const l = lines[i] || {};
    const count = Math.max(toNum(l.orderCount), 1);

    // default / other tax (Android payloadTaxScaleRatio + useBasePerItemDefaultTax)
    const dtt = toNum(it.default_tax_total);
    if (useBasePerItem) it.default_tax_total = money(toNum(baseMap.get(l.orderId)) / count);
    else if (ratio !== 1) {
      cumCreditTax += dtt;
      const nc = r2(cumCreditTax * ratio);
      it.default_tax_total = money(nc - cumScaledTax);
      cumScaledTax = nc;
    }
    if (ratio !== 1) {
      it.other_tax_total = money(toNum(it.other_tax_total) * ratio);
      if (it.other_tax_desc) {
        try {
          const d = JSON.parse(it.other_tax_desc);
          for (const k of Object.keys(d)) d[k] = r2(toNum(d[k]) * ratio);
          it.other_tax_desc = JSON.stringify(d);
        } catch {
          /* leave malformed as-is */
        }
      }
    }

    // price ÷M when the credit lines are not consistent with the tender (cash + wallet deduction)
    if (!creditDualConsistent && pct > 0) it.price = money(toNum(it.price) / M);

    if (divisor !== 1) {
      for (const key of ["discount_amt", "coupon_code_amt", "custom_discount_amt", "bogo_discount", "mix_match_amt", "adjust_price"]) {
        if (it[key] == null) continue;
        const v = toNum(it[key]);
        if (!Number.isFinite(v) || v === 0) continue;
        let conv;
        if (key === "adjust_price") conv = v; // showCreditListUnit: inventory_price stays the credit list
        else if ((key === "coupon_code_amt" || key === "custom_discount_amt") && coupon > 0) {
          cumOrdCredit += v;
          const nb = r2((toNum(dual.baseOrderDiscount) * cumOrdCredit) / coupon);
          conv = nb - cumOrdBase;
          cumOrdBase = nb;
        } else conv = v / divisor;
        it[key] = money(conv);
      }
      const couponCredit = toNum(it.item_coupon_discount_amount);
      if (couponCredit !== 0) {
        const cb = r2(couponCredit / divisor);
        it.item_coupon_discount_amount = cb;
        if (it.item_coupon_json && typeof it.item_coupon_json === "object") it.item_coupon_json.discount_amount = cb;
      }
    }
    if (creditCardScale && coupon > 0) {
      // order-level coupon/custom → running-total share of getCardOrderDiscount (others unchanged)
      for (const key of ["coupon_code_amt", "custom_discount_amt"]) {
        const v = toNum(it[key]);
        if (!v) continue;
        cumOrdCard += v;
        const nc = r2((toNum(dual.cardOrderDiscount) * cumOrdCard) / coupon);
        it[key] = money(nc - cumOrdCardRounded);
        cumOrdCardRounded = nc;
      }
    }

    const notLottery = l.isLotteryTicket !== "1";
    if (notLottery && divisor !== 1 && (totalCreditNet > 0 || toNum(it.discount_amt) > 0 || toNum(it.item_coupon_discount_amount) > 0)) {
      cumNetDist += Math.max(toNum(l.jsonNewPrice) - toNum(l.jsonLoyaltyAmt) * lpNcaFraction, 0);
      const nb = totalCreditNetLp > 0 ? r2((totalBaseNetLp * cumNetDist) / totalCreditNetLp) : 0;
      const baseNet = Math.max(nb - cumBaseNet, 0);
      cumBaseNet = nb;
      it.price = money(baseNet + toNum(it.discount_amt) + toNum(it.item_coupon_discount_amount));
    } else if (notLottery && creditCardScale && totalCreditNet > 0) {
      cumCardNetIn += toNum(l.jsonNewPrice);
      const nc = r2((totalCardNet * cumCardNetIn) / totalCreditNet);
      const cardNet = Math.max(nc - cumCardNet, 0);
      cumCardNet = nc;
      it.price = money(cardNet + toNum(it.discount_amt) + toNum(it.item_coupon_discount_amount));
    }
  });
  return { cashPayDiscount: r2(shares.reduce((a, b) => a + b, 0)) };
}

/**
 * Order-level dual fields (Android getPayloadSubTotal / getPayloadDiscount / setPayData).
 * @returns null when the store is not in dual-price mode.
 */
export function dualOrderFields(p = {}) {
  const { cfg, dual, kind, lines = [], subtotalField = 0, priceDiscount = 0, lpScGc = 0, mGrandTotal = 0, totalNca = 0, cashPayDiscount = 0, split = null } = p;
  if (!isDualPrice(cfg) || !dual) return null;
  const pct = toNum(cfg.pct);
  const M = surchargeMultiplier(cfg);
  const credit = kind === "credit";
  const isSplit = kind === "split";
  const giftFace = giftCardFaceTotal(cfg, lines);
  const baseSubtotal = toBasePrice(cfg, toNum(subtotalField) - giftFace) + giftFace;

  let subtotal;
  if (!isSplit && credit) subtotal = r2(toNum(subtotalField));
  else if (!isSplit) subtotal = r2(baseSubtotal);
  else subtotal = split ? r2(split.payloadSubTotal) : r2(toNum(subtotalField) - toNum(cashPayDiscount));

  let disc;
  if (kind === "split") disc = split ? r2(split.blendPriceDiscount) : r2(priceDiscount);
  else if (!credit && pct > 0) disc = r2(toNum(priceDiscount) / M);
  else if (credit && dual.cardDerived) disc = r2(subtotal + toNum(dual.cardTax) - toNum(dual.cardGrandTotal));
  else disc = r2(toNum(priceDiscount) + (credit ? toNum(mGrandTotal) - toNum(dual.cardGrandTotal) : 0));

  return {
    subtotal,
    discount: r2(disc + toNum(lpScGc)),
    cashDiscounting: isSplit ? r2(totalNca) : credit ? toNum(dual.creditPriceNca) : 0,
    cashPayDiscount: r2(cashPayDiscount),
    nca: isSplit ? r2(totalNca) : credit ? toNum(dual.creditPriceNca) : 0,
    // getLoyaltyJson amounts: non-credit one-shot ÷M (coupon via toBasePrice), credit raw.
    scaleCouponJson: !isSplit && !credit && pct > 0,
  };
}

// ── Split (Phase C) — Android isSplitBlendPayload: dual + split + no redeemed gift card ────────────

const isNca0 = (t) => !(toNum(t && t.ncaAmount) > 0);

/** Android splitCashBaseFraction: cash-side share of the base paid (exempt SC/payouts follow the last leg). */
export function splitCashBaseFraction(tenders, exemptFace = 0) {
  const legs = (tenders || []).filter(Boolean);
  const totalBase = legs.reduce((s, t) => s + toNum(t.amount), 0) + toNum(exemptFace);
  if (totalBase <= 0) return 0;
  const last = legs[legs.length - 1];
  const exemptOnCash = last && isNca0(last) ? toNum(exemptFace) : 0;
  const cashBase = legs.filter(isNca0).reduce((s, t) => s + toNum(t.amount), 0) + exemptOnCash;
  return Math.min(Math.max(cashBase / totalBase, 0), 1);
}

/** Android getSubTotals(listOf(row)) — override price for a plain override, else list; minus item discount. */
function lineSubTotal(l) {
  if (l.isLotteryPayout || l.isScratcherPayout || l.isLotteryTicket === "1") return 0;
  const count = toNum(l.orderCount || 1);
  const isOverride = toNum(l.orderUnitNewPrice) > 0 && toNum(l.bogoIdentifier) === 0 && !l.isBulkPriceApplied;
  const unit = isOverride ? toNum(l.orderUnitNewPrice) : toNum(l.catalogUnitPrice != null ? l.catalogUnitPrice : (l.model || {}).price);
  const pct = toNum(l.orderDiscountPercent);
  const disc = toNum(l.orderDiscount) > 0 ? toNum(l.orderDiscount) * count : pct > 0 ? Math.trunc(((unit * pct) / 100) * 100) / 100 : 0;
  return unit * count - disc;
}

/**
 * Rewrite item_ses rows for a dual-price SPLIT order (Android toParseJson isSplitBlendPayload block).
 * Discount fields are first brought to the cash base (as for a non-credit tender), then blended by
 * K = 1 + cardFraction x pct/100. NOTE: Android scales them by the LAST leg's tender instead (credit
 * last => credit space), which leaves the order-discount sum off the blend target; the web always
 * uses the base.
 */
export function applyDualSplitBlend(items, lines, p = {}) {
  const {
    cfg,
    dual,
    tenders = [],
    storeCredit = 0,
    lotteryScratcher = 0,
    couponDiscountValue = 0,
    finalDiscount = null,
    taxRateInAmount = 0,
    subtotalField = 0,
    bogoTotal = 0,
    mixMatchTotal = 0,
  } = p;
  const pct = toNum(cfg && cfg.pct);
  const M = surchargeMultiplier(cfg);
  const exemptFace = r2(toNum(storeCredit) + toNum(lotteryScratcher));
  const cashFraction = splitCashBaseFraction(tenders, exemptFace);
  const cardFraction = Math.min(Math.max(1 - cashFraction, 0), 1);
  const K = pct > 0 ? 1 + (cardFraction * pct) / 100 : 1;
  const pointsPaid = lines.reduce((s, l) => s + toNum(l.pointsPaidNcaWaived), 0);

  // item_nca_amt — the card share of each line's premium
  items.forEach((it, i) => {
    const l = lines[i] || {};
    if (isPayoutOrGift(l)) {
      it.item_nca_amt = "0.00";
      return;
    }
    const u = toNum(l.jsonNewPrice) / Math.max(toNum(l.orderCount), 1);
    it.item_nca_amt = money((u - toBasePrice(cfg, u)) * cardFraction);
  });

  // item_cash_pay_discount — splitBlendItemNca x cashFraction over getSubTotals(row) weights
  const creditNet = toNum(subtotalField);
  const blendItemNca = pct > 0 ? Math.max(r2(creditNet - toBasePrice(cfg, creditNet) - pointsPaid), 0) : 0;
  const cashTotal = Math.max(r2(blendItemNca * cashFraction), 0);
  const shares = distribute(
    cashTotal,
    lines.map((l) => (toNum(l.pointsPaidNcaWaived) > 0 ? 0 : lineSubTotal(l)))
  );
  items.forEach((it, i) => {
    it.item_cash_pay_discount = money(shares[i] || 0);
  });
  const cashPayDiscount = r2(shares.reduce((a, b) => a + b, 0));

  // bring the discount fields to the cash base first (Android payloadTaxDivisor = M branch)
  const coupon = toNum(couponDiscountValue);
  let cumOrdCredit = 0;
  let cumOrdBase = 0;
  items.forEach((it) => {
    for (const key of ["discount_amt", "coupon_code_amt", "custom_discount_amt", "bogo_discount", "mix_match_amt"]) {
      if (it[key] == null) continue;
      const v = toNum(it[key]);
      if (!v) continue;
      if ((key === "coupon_code_amt" || key === "custom_discount_amt") && coupon > 0) {
        cumOrdCredit += v;
        const nb = r2((toNum(dual.baseOrderDiscount) * cumOrdCredit) / coupon);
        it[key] = money(nb - cumOrdBase);
        cumOrdBase = nb;
      } else {
        it[key] = money(v / M);
      }
    }
  });

  // blend targets (Android splitBlendOrderDiscount / splitBlendPriceDiscount)
  const blendOrderDiscount = pct > 0 ? r2(toNum(dual.baseOrderDiscount) * K) : toNum(dual.baseOrderDiscount);
  const carried = coupon > 0 ? Math.min(Math.max(toNum(finalDiscount != null ? finalDiscount : coupon) / coupon, 0), 1) : 0;
  const blendPriceDiscount = Math.max(
    r2(r2(blendOrderDiscount * carried) + r2(((toNum(bogoTotal) + toNum(mixMatchTotal)) / M) * K)),
    0
  );
  const blendDiscBaseTotal = toNum(dual.baseOrderDiscount);

  let cumDiscBase = 0;
  let cumDiscRounded = 0;
  let cumDealBase = 0;
  let cumDealRounded = 0;
  items.forEach((it, i) => {
    const l = lines[i] || {};
    const qty = Math.max(toNum(l.orderCount), 1);
    const isOverride = toNum(l.orderUnitNewPrice) > 0 && toNum(l.bogoIdentifier) === 0 && !l.isBulkPriceApplied;
    const listUnit = isOverride ? toNum(l.orderUnitNewPrice) : toNum(l.catalogUnitPrice != null ? l.catalogUnitPrice : (l.model || {}).price);
    const creditUnit = Math.max(listUnit - toNum(l.pointsPaidNcaWaived) / qty, 0);
    const pctD = toNum(l.orderDiscountPercent);
    const discLine =
      toNum(l.orderDiscount) > 0 ? toNum(l.orderDiscount) * qty : pctD > 0 ? Math.trunc(((creditUnit * pctD) / 100) * 100) / 100 : 0;
    const discUnit = discLine / qty;
    const creditNetUnit = Math.max(creditUnit - discUnit, 0);
    const blendedNetUnit = Math.max(creditNetUnit - (shares[i] || 0) / qty, 0);
    const creditUnitRounded = r2(creditUnit);
    const listCreditUnit = r2(toNum(l.orderUnitPrice));
    if (isOverride && listCreditUnit > 0 && listCreditUnit !== creditUnitRounded) {
      it.inventory_price = money(listCreditUnit);
      it.adjust_price = money(listCreditUnit - creditUnitRounded);
    } else {
      it.inventory_price = money(creditUnitRounded);
    }
    const couponCreditUnit = r2(toNum(l.couponDiscountFinalAmt));
    if (couponCreditUnit !== 0) {
      it.item_coupon_discount_amount = couponCreditUnit;
      if (it.item_coupon_json && typeof it.item_coupon_json === "object") it.item_coupon_json.discount_amount = couponCreditUnit;
    }
    const manualDiscUnit = Math.max(discUnit - couponCreditUnit, 0);
    if (manualDiscUnit !== 0) it.discount_amt = money(manualDiscUnit);

    let orderDiscBlendUnit = 0;
    if (blendDiscBaseTotal > 0) {
      const custBase = toNum(it.custom_discount_amt);
      const coupBase = toNum(it.coupon_code_amt);
      const lineBase = custBase + coupBase;
      if (lineBase !== 0) {
        cumDiscBase += lineBase;
        const nc = r2((cumDiscBase / blendDiscBaseTotal) * blendOrderDiscount);
        const lineBlend = nc - cumDiscRounded;
        cumDiscRounded = nc;
        if (coupBase === 0) it.custom_discount_amt = money(lineBlend);
        else if (custBase === 0) it.coupon_code_amt = money(lineBlend);
        else {
          const custPart = r2((lineBlend * custBase) / lineBase);
          it.custom_discount_amt = money(custPart);
          it.coupon_code_amt = money(lineBlend - custPart);
        }
        orderDiscBlendUnit = lineBlend / qty;
      }
    }
    let dealBlendUnit = 0;
    const dealKey = toNum(it.bogo_discount) !== 0 ? "bogo_discount" : toNum(it.mix_match_amt) !== 0 ? "mix_match_amt" : null;
    if (dealKey) {
      const raw = toNum(it[dealKey]);
      const sign = raw < 0 ? -1 : 1;
      cumDealBase += Math.abs(raw);
      const nc = r2(cumDealBase * K);
      const lineDeal = nc - cumDealRounded;
      cumDealRounded = nc;
      it[dealKey] = money(lineDeal * sign);
      dealBlendUnit = lineDeal / qty;
    }
    const loyaltyUnit = r2(toNum(l.jsonLoyaltyAmt) / qty);
    it.price = money(Math.max(blendedNetUnit - orderDiscBlendUnit - dealBlendUnit - loyaltyUnit + discUnit, 0));
  });

  // tax — Android payloadTaxScaleRatio split arm:
  //   (charged base+NCA + exempt − (payloadSub − blendPriceDiscount − ΣLP)) / creditTax
  const payloadSubTotal = r2(creditNet - pointsPaid - cashPayDiscount);
  const charged = r2(tenders.reduce((s, t) => s + toNum(t.amount) + toNum(t.ncaAmount), 0) + exemptFace);
  const lpTotal = r2(
    lines.reduce((s, l) => {
      const q = Math.max(toNum(l.orderCount), 1);
      return s + r2(toNum(l.jsonLoyaltyAmt) / q) * q;
    }, 0)
  );
  const creditTax = toNum(taxRateInAmount);
  const ratio = creditTax > 0 ? Math.max(charged - (payloadSubTotal - blendPriceDiscount - lpTotal), 0) / creditTax : 1;
  if (ratio !== 1) {
    let cumCredit = 0;
    let cumScaled = 0;
    items.forEach((it) => {
      cumCredit += toNum(it.default_tax_total);
      const nc = r2(cumCredit * ratio);
      it.default_tax_total = money(nc - cumScaled);
      cumScaled = nc;
      it.other_tax_total = money(toNum(it.other_tax_total) * ratio);
      if (it.other_tax_desc) {
        try {
          const d = JSON.parse(it.other_tax_desc);
          for (const k of Object.keys(d)) d[k] = r2(toNum(d[k]) * ratio);
          it.other_tax_desc = JSON.stringify(d);
        } catch {
          /* leave malformed as-is */
        }
      }
    });
  }
  return { cashPayDiscount, blendPriceDiscount, blendOrderDiscount, cashFraction, K, payloadSubTotal, carried };
}
